import React, { FunctionComponent, useRef, useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { EnumerateLogo } from '../Dashboard/Icons';
import ProfileMenu from '../Common/ProfileMenu';
import {
  longFormatPermissionType,
  surveyPermissionsObjType,
  usagePermissionsObjType,
} from '@/store/Reducers/Types';
import { TabType } from './Tabs';
import useCobranding from '@/hooks/useCobranding';
import Colab from '../Common/Colab';
import { useRouter } from 'next/router';
import AccountActions from '../Common/AccountActions';
import { HugeiconsIcon } from '@hugeicons/react';
import { SmallLogo } from 'icons/logo/small';

export type SidebarProps = {
  tabId: string;
  surveyId: number;
  permissions?: any;
  Tabs: TabType[];
};

const Sidebar: FunctionComponent<SidebarProps> = ({
  Tabs,
  tabId,
  surveyId,
  permissions,
}) => {
  const router = useRouter();
  const { isCobranding, brandLogo } = useCobranding();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, opacity: 0 });

  const checkForPermissions =
    router.pathname !== '/settings' && router.pathname !== '/templates';

  const hasPermission = (tab: TabType) => {
    if (!checkForPermissions) return true;
    else {
      if (!permissions) return false;
    }
    if (tab.id === 'my-usage') {
      return true;
    }
    if (tab.id === 'clients-data' || tab.id === 'client-management') {
      return permissions['admin'] === true;
    }
    if (!tab.permission || tab.permission === '') {
      return true;
    }
    return permissions[tab.permission] === true;
  };

  // Get visible tabs
  const visibleTabs = useMemo(() => {
    return Tabs.filter((tab) => hasPermission(tab));
  }, [Tabs, permissions, checkForPermissions]);

  // Update indicator position when tabId changes
  useEffect(() => {
    const updateIndicator = () => {
      const selectedTab = tabRefs.current[tabId];
      const container = tabsContainerRef.current;

      if (selectedTab && container) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = selectedTab.getBoundingClientRect();
        const top = tabRect.top - containerRect.top + container.scrollTop;

        setIndicatorStyle({
          top,
          opacity: 1,
        });
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(updateIndicator, 10);
    return () => clearTimeout(timer);
  }, [tabId, visibleTabs]);

  return (
    <div className="box-border flex w-[75px] h-screen flex-col border-r border-b-indigo-100">
      {/* Fixed Header */}
      <header className="h-12 w-full flex items-center justify-center border-b border-border-color flex-shrink-0">
        <Link href="/home">
          <SmallLogo />
        </Link>
      </header>

      {/* Scrollable Tabs Section */}
      <div
        ref={tabsContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden w-full flex flex-col items-center py-3 sidebar-scroll relative"
      >
        {/* Sliding Indicator */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[52px] h-[44px] rounded-lg bg-gradient-to-br from-link-blue to-link-blue-hover shadow-md shadow-[0_4px_12px_rgba(85,121,242,0.25)] transition-all duration-300 ease-out z-0"
          style={{
            top: indicatorStyle.top,
            opacity: indicatorStyle.opacity,
          }}
        />

        <div className="space-y-3 w-full relative z-10">
          {visibleTabs.map((tab) => (
            <Link
              key={tab.id}
              href={tab.route(tab.id || '', `${surveyId || ''}`)}
              className="block"
            >
              <div
                ref={(el) => (tabRefs.current[tab.id] = el)}
                id={tab.id}
                className="flex justify-center flex-col items-center group"
              >
                <div className="relative">
                  {tab.beta && (
                    <span className="absolute bottom-0 -right-3 text-[8px] font-bold text-white bg-violet-500 px-1 rounded z-10">
                      BETA
                    </span>
                  )}
                  <div
                    className={`box-content flex justify-center items-center w-7 h-5 p-3 object-contain rounded-lg cursor-pointer transition-all duration-200 ease-out ${
                      tabId !== tab.id
                        ? 'hover:bg-slate-100 hover:shadow-sm hover:scale-105 active:scale-95'
                        : 'scale-105'
                    }`}
                  >
                    <HugeiconsIcon
                      icon={tab.Icon}
                      size={24}
                      color={tabId === tab.id ? '#FFFFFF' : '#4a4a4a'}
                      className="transition-colors duration-200"
                    />
                  </div>
                </div>
                <span
                  className={`w-full flex px-2 justify-center mt-1 sub-heading-hero-2 !text-xs text-center text-wrap transition-all duration-200 ${
                    tabId === tab.id
                      ? 'text-link-blue font-semibold'
                      : 'text-primary-text-color'
                  }`}
                >
                  {tab.text}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="flex flex-col items-center justify-between space-y-4 py-4 flex-shrink-0">
        {isCobranding && <Colab />}
        <AccountActions isLarge={false} />
      </div>
    </div>
  );
};

export default Sidebar;
